/**
 * UsersContent - User Management Panel
 * 
 * Admin-only panel for managing Cognito users and invitations.
 * Accessible via Cmd+K, @go users, or TopBar menu.
 */

import { useState, useEffect } from 'react';
import { authService, AuthUser } from '../../services/auth-service';
import { logger } from '../../utils/logger';
import { invitationsApi } from '../../services/api';
import axios from 'axios';
import ConfirmationModal from '../ConfirmationModal';
import Pagination from '../Pagination';
import { SearchInput } from '../SearchInput';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
  Input,
  Label,
} from '@/components/ui';

interface CognitoUser {
  username: string;
  email: string;
  enabled: boolean;
  status: string;
  createdAt?: string;
  modifiedAt?: string;
  attributes?: Record<string, string>;
  groups?: string[];
}

interface Invitation {
  inviteId: string;
  email: string;
  role: string;
  department?: string;
  status: 'Pending' | 'Accepted' | 'Expired' | 'Revoked';
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  acceptedAt?: string;
  inviteToken?: string;
}

const API_BASE = `${import.meta.env?.VITE_API_URL || '/api'}/v1`;

const roleGroups = [
  'Administrators',
  'QualityManagers',
  'EngineeringManagers',
  'Engineers',
  'ProductionManagers',
  'Viewers',
];

export function UsersContent() {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('');
  const [newUserDepartment, setNewUserDepartment] = useState('');

  // Confirmation modal states
  const [showResendModal, setShowResendModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<Invitation | null>(null);
  const [selectedUser, setSelectedUser] = useState<CognitoUser | null>(null);
  
  // Edit user modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<CognitoUser | null>(null);
  const [editDepartment, setEditDepartment] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  
  // Pagination and view states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    checkAuth();
    loadUsers();
    loadInvitations();
  }, []);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (message?.type === 'success') {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const checkAuth = async () => {
    try {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
      
      if (!user || !user.groups?.includes('Administrators')) {
        setError('Administrator access required');
      }
    } catch (_err) {
      setError('Not authenticated');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const tokens = await authService.getTokens();
      const response = await axios.get(`${API_BASE}/users`, {
        headers: {
          Authorization: `Bearer ${tokens?.idToken}`,
        },
      });
      setUsers(response.data.data);
      setError(null);
    } catch (err: unknown) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const data = await invitationsApi.list();
      setInvitations(data);
    } catch (err: unknown) {
      logger.error('Failed to load invitations:', err);
    } finally {
      setInvitationsLoading(false);
    }
  };

  // Filter users by search (email, username, department, or role)
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const groupsString = user.groups?.join(' ').toLowerCase() || '';
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.username?.toLowerCase().includes(searchLower) ||
      user.attributes?.['custom:department']?.toLowerCase().includes(searchLower) ||
      groupsString.includes(searchLower)
    );
  });

  // Paginate users
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleInviteUser = async () => {
    try {
      await invitationsApi.create(newUserEmail, newUserRole, newUserDepartment);
      setShowInviteModal(false);
      setNewUserEmail('');
      setNewUserRole('');
      setNewUserDepartment('');
      loadInvitations();
      setMessage({ type: 'success', text: 'Invitation sent successfully!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message });
    }
  };

  const handleResendInvite = (inviteId: string) => {
    const invite = invitations.find(i => i.inviteId === inviteId);
    if (invite) {
      setSelectedInvite(invite);
      setShowResendModal(true);
    }
  };

  const confirmResendInvite = async () => {
    if (!selectedInvite) return;
    
    try {
      await invitationsApi.resend(selectedInvite.inviteId);
      setShowResendModal(false);
      setSelectedInvite(null);
      loadInvitations();
      setMessage({ type: 'success', text: 'Invitation resent successfully!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err.message });
      throw err;
    }
  };

  const handleRevokeInvite = (inviteId: string) => {
    const invite = invitations.find(i => i.inviteId === inviteId);
    if (invite) {
      setSelectedInvite(invite);
      setShowRevokeModal(true);
    }
  };

  const confirmRevokeInvite = async () => {
    if (!selectedInvite) return;
    
    try {
      await invitationsApi.revoke(selectedInvite.inviteId);
      setShowRevokeModal(false);
      setSelectedInvite(null);
      loadInvitations();
      setMessage({ type: 'success', text: 'Invitation revoked successfully!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err.message });
      throw err;
    }
  };

  const handleCopyInviteLink = (token: string) => {
    const link = `${window.location.origin}/onboard?token=${token}`;
    navigator.clipboard.writeText(link);
    setMessage({ type: 'success', text: 'Invitation link copied to clipboard!' });
  };

  const handleDeleteUser = (username: string) => {
    const user = users.find(u => u.username === username);
    if (user) {
      setSelectedUser(user);
      setShowDeleteUserModal(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    const tokens = await authService.getTokens();
    await axios.delete(`${API_BASE}/users/${selectedUser.username}`, {
      headers: {
        Authorization: `Bearer ${tokens?.idToken}`,
      },
    });
    setShowDeleteUserModal(false);
    setSelectedUser(null);
    loadUsers();
  };

  const handleUpdateGroups = async (username: string, groups: string[]) => {
    try {
      const tokens = await authService.getTokens();
      await axios.patch(
        `${API_BASE}/users/${username}`,
        { groups },
        {
          headers: {
            Authorization: `Bearer ${tokens?.idToken}`,
          },
        }
      );
      loadUsers();
    } catch (err: unknown) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleEditUser = (user: CognitoUser) => {
    setEditingUser(user);
    setEditDepartment(user.attributes?.['custom:department'] || '');
    setEditRole(user.groups?.[0] || '');
    setEditEnabled(user.enabled);
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setEditSaving(true);
    try {
      const tokens = await authService.getTokens();
      
      // Build the update payload
      const updatePayload: {
        attributes?: Record<string, string>;
        groups?: string[];
        enabled?: boolean;
      } = {};
      
      // Update department if changed
      const currentDepartment = editingUser.attributes?.['custom:department'] || '';
      if (editDepartment !== currentDepartment) {
        updatePayload.attributes = { 'custom:department': editDepartment };
      }
      
      // Update role if changed
      const currentRole = editingUser.groups?.[0] || '';
      if (editRole !== currentRole) {
        updatePayload.groups = editRole ? [editRole] : [];
      }
      
      // Update enabled status if changed
      if (editEnabled !== editingUser.enabled) {
        updatePayload.enabled = editEnabled;
      }
      
      // Only make API call if there are changes
      if (Object.keys(updatePayload).length > 0) {
        await axios.patch(
          `${API_BASE}/users/${editingUser.username}`,
          updatePayload,
          {
            headers: {
              Authorization: `Bearer ${tokens?.idToken}`,
            },
          }
        );
        
        setMessage({ type: 'success', text: 'User updated successfully!' });
        loadUsers();
      }
      
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setEditSaving(false);
    }
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days ago`;
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else {
      return `In ${diffDays} days`;
    }
  };

  // Access denied state
  if (!currentUser || !currentUser.groups?.includes('Administrators')) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
          <h2 className="text-sm font-medium text-foreground flex-1">
            User Management
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center bg-card">
          <div className="text-center">
            <div className="text-4xl mb-4">🔒</div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Access Denied</h3>
            <p className="text-muted-foreground">Administrator access required to manage users.</p>
          </div>
        </div>
      </div>
    );
  }

  const pendingInvites = invitations.filter(inv => inv.status === 'Pending');

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header - matches DocumentViewerContent styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <h2 className="text-sm font-medium text-foreground flex-1">
          User Management
        </h2>
        <Button
          onClick={() => setShowInviteModal(true)}
          size="sm"
          className="flex items-center gap-2"
        >
          <span>📧</span>
          <span>Invite User</span>
        </Button>
      </div>

      {/* Content - matches DocumentViewerContent styling */}
      <div className="flex-1 overflow-y-auto p-4 bg-card space-y-4">
        {/* Quick Stats */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex gap-6 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Active Users:</span>
              <span className="font-semibold text-foreground">{users.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Pending Invitations:</span>
              <span className="font-semibold text-warning">{pendingInvites.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Administrators:</span>
              <span className="font-semibold text-link">{users.filter(u => u.groups?.includes('Administrators')).length}</span>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {message && (
          <div className={`p-3 rounded-lg flex items-center justify-between ${
            message.type === 'success' 
              ? 'bg-success-light border border-success text-success'
              : 'bg-destructive-light border border-destructive text-destructive'
          }`}>
            <p className="text-sm">{message.text}</p>
            <Button 
              onClick={() => setMessage(null)}
              variant="ghost"
              size="icon"
              className="text-current opacity-70 hover:opacity-100 ml-4 h-auto w-auto p-1"
            >
              ✕
            </Button>
          </div>
        )}

        {error && !message && (
          <div className="p-3 bg-destructive-light border border-destructive rounded-lg text-destructive">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Invitations Section */}
        <div className="bg-muted/30 rounded-lg">
          <div className="px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">
              Pending Invitations ({pendingInvites.length})
            </h3>
          </div>
          
          {invitationsLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading invitations...
            </div>
          ) : invitations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p className="mb-2">No invitations yet</p>
              <p className="text-sm text-muted-foreground">
                Click "Invite User" to send your first invitation
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-muted/30">
                  <tr className="text-left">
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Email</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Role</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Department</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Invited By</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Sent</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Expires</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invite) => (
                    <tr key={invite.inviteId} className="hover:bg-muted/50">
                      <td className="p-3 text-sm text-foreground">{invite.email}</td>
                      <td className="p-3 text-sm text-foreground">{invite.role}</td>
                      <td className="p-3 text-sm text-muted-foreground">{invite.department || '-'}</td>
                      <td className="p-3 text-sm text-muted-foreground">{invite.invitedBy}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {formatRelativeTime(invite.invitedAt)}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {formatRelativeTime(invite.expiresAt)}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          invite.status === 'Pending' ? 'bg-warning-light text-warning' :
                          invite.status === 'Accepted' ? 'bg-success-light text-success' :
                          invite.status === 'Expired' ? 'bg-muted text-muted-foreground' : 
                          'bg-destructive-light text-destructive'
                        }`}>
                          {invite.status}
                        </span>
                      </td>
                      <td className="p-3">
                        {invite.status === 'Pending' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleResendInvite(invite.inviteId)}
                              size="xs"
                              title="Resend invitation email"
                            >
                              📧 Resend
                            </Button>
                            <Button
                              onClick={() => invite.inviteToken && handleCopyInviteLink(invite.inviteToken)}
                              variant="success"
                              size="xs"
                              title="Copy invitation link"
                            >
                              🔗 Copy
                            </Button>
                            <Button
                              onClick={() => handleRevokeInvite(invite.inviteId)}
                              variant="destructive"
                              size="xs"
                              title="Revoke invitation"
                            >
                              ✕ Revoke
                            </Button>
                          </div>
                        )}
                        {invite.status === 'Accepted' && (
                          <span className="text-success text-sm">✓ Completed</span>
                        )}
                        {(invite.status === 'Expired' || invite.status === 'Revoked') && (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Active Users Section */}
        <div className="bg-muted/30 rounded-lg">
          <div className="px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">
              Active Users ({users.length})
            </h3>
          </div>
          
          {/* Search */}
          <div className="p-4">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by email, username, department, or role..."
            />
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {searchTerm ? 'No users match your search.' : 'No users found.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead className="bg-muted/30">
                    <tr className="text-left">
                      <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Email</th>
                      <th className="p-3 text-xs font-semibold text-muted-foreground uppercase w-[140px]">Status</th>
                      <th className="p-3 text-xs font-semibold text-muted-foreground uppercase w-[200px]">Role</th>
                      <th className="p-3 text-xs font-semibold text-muted-foreground uppercase w-[150px]">Department</th>
                      <th className="p-3 text-xs font-semibold text-muted-foreground uppercase w-[120px]">Created</th>
                      <th className="p-3 text-xs font-semibold text-muted-foreground uppercase w-[160px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => {
                      const isSystemAdmin = user.email === 'admin@aerotage.com';
                      
                      return (
                        <tr 
                          key={user.username} 
                          className={`hover:bg-muted/50 transition-colors ${
                            isSystemAdmin ? 'bg-info-light' : ''
                          }`}
                        >
                          <td className="p-3 text-sm text-foreground">
                            {user.email}
                            {isSystemAdmin && (
                              <span className="inline-block ml-2 px-2 py-0.5 bg-success text-success-foreground rounded text-xs font-semibold">
                                🛡️ System Admin
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              user.status === 'CONFIRMED' 
                                ? 'bg-success-light text-success border border-success' 
                                : 'bg-warning-light text-warning border border-warning dark:border-warning'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="p-3">
                            {/* eslint-disable-next-line no-restricted-syntax -- Native select for role dropdown, shadcn Select would require significant refactoring */}
                            <select
                              value={user.groups?.[0] || ''}
                              onChange={(e) => handleUpdateGroups(user.username, e.target.value ? [e.target.value] : [])}
                              disabled={isSystemAdmin && user.groups?.includes('Administrators')}
                              className={`py-1.5 px-3 rounded border border-border bg-card text-foreground font-medium text-sm transition-colors ${
                                isSystemAdmin && user.groups?.includes('Administrators') ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                              title={isSystemAdmin && user.groups?.includes('Administrators') ? 'System admin must always have Administrator role' : ''}
                            >
                              <option value="">No Role</option>
                              {roleGroups.map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {user.attributes?.['custom:department'] || '-'}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleEditUser(user)}
                                variant="outline"
                                size="xs"
                              >
                                Edit
                              </Button>
                              {!isSystemAdmin ? (
                                <Button
                                  onClick={() => handleDeleteUser(user.username)}
                                  variant="destructive"
                                  size="xs"
                                >
                                  Delete
                                </Button>
                              ) : (
                                <span className="text-muted-foreground text-sm italic">
                                  Protected
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="p-4">
                <Pagination
                  currentPage={currentPage}
                  totalItems={filteredUsers.length}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemName="users"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Invite User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
            <DialogDescription>
              Send a secure invitation link via email. The user will set their own password during onboarding.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email *</Label>
              <Input
                id="invite-email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role *</Label>
              {/* eslint-disable-next-line no-restricted-syntax -- Native select for role picker, shadcn Select would require significant refactoring */}
              <select
                id="invite-role"
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
                className="w-full p-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select Role</option>
                {roleGroups.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invite-department">Department</Label>
              <Input
                id="invite-department"
                type="text"
                value={newUserDepartment}
                onChange={(e) => setNewUserDepartment(e.target.value)}
                placeholder="e.g., Avionics Engineering"
              />
            </div>
            
            <div className="bg-info-light border border-info rounded-lg p-4 text-sm">
              <div className="font-semibold text-info mb-2">What happens next:</div>
              <ul className="m-0 pl-5 text-info space-y-1">
                <li>Invitation email sent with secure link</li>
                <li>Link expires in 7 days</li>
                <li>User sets their own password</li>
                <li>You can resend or revoke anytime</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowInviteModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteUser}
              disabled={!newUserEmail || !newUserRole}
            >
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Invitation Modal */}
      {showResendModal && selectedInvite && (
        <ConfirmationModal
          title="Resend Invitation"
          message="A new invitation link will be generated and the old link will be invalidated."
          confirmText="Resend Invitation"
          cancelText="Cancel"
          isDanger={false}
          onConfirm={confirmResendInvite}
          onCancel={() => {
            setShowResendModal(false);
            setSelectedInvite(null);
          }}
        >
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="mb-2">
              <strong>Email:</strong> {selectedInvite.email}
            </div>
            <div className="mb-2">
              <strong>Role:</strong> {selectedInvite.role}
            </div>
            <div>
              <strong>Invited:</strong> {new Date(selectedInvite.invitedAt).toLocaleDateString()}
            </div>
          </div>
        </ConfirmationModal>
      )}

      {/* Revoke Invitation Modal */}
      {showRevokeModal && selectedInvite && (
        <ConfirmationModal
          title="Revoke Invitation"
          message="The invitation link will no longer work. The user will not be able to accept this invitation."
          confirmText="Revoke Invitation"
          cancelText="Cancel"
          isDanger={true}
          onConfirm={confirmRevokeInvite}
          onCancel={() => {
            setShowRevokeModal(false);
            setSelectedInvite(null);
          }}
        >
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="mb-2">
              <strong>Email:</strong> {selectedInvite.email}
            </div>
            <div className="mb-2">
              <strong>Role:</strong> {selectedInvite.role}
            </div>
            <div>
              <strong>Status:</strong> <span className="px-2 py-0.5 bg-warning-light text-warning rounded text-sm">{selectedInvite.status}</span>
            </div>
          </div>
        </ConfirmationModal>
      )}

      {/* Delete User Modal */}
      {showDeleteUserModal && selectedUser && (
        <ConfirmationModal
          title="Delete User"
          message="Are you sure you want to permanently delete this user? This action cannot be undone."
          confirmText="Delete User"
          cancelText="Cancel"
          isDanger={true}
          onConfirm={confirmDeleteUser}
          onCancel={() => {
            setShowDeleteUserModal(false);
            setSelectedUser(null);
          }}
        >
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="mb-2">
              <strong>Username:</strong> {selectedUser.username}
            </div>
            <div className="mb-2">
              <strong>Email:</strong> {selectedUser.email}
            </div>
            <div>
              <strong>Status:</strong> <span className={`px-2 py-0.5 rounded text-sm ${selectedUser.enabled ? 'bg-success-light text-success' : 'bg-destructive-light text-destructive'}`}>{selectedUser.status}</span>
            </div>
          </div>
        </ConfirmationModal>
      )}

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowEditModal(false);
          setEditingUser(null);
        }
      }}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and permissions.
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4 py-4">
              {/* Read-only user info */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Email:</span>
                  <span className="text-sm font-medium text-foreground">{editingUser.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Username:</span>
                  <span className="text-sm font-medium text-foreground">{editingUser.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Account Status:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    editingUser.status === 'CONFIRMED' 
                      ? 'bg-success-light text-success' 
                      : 'bg-warning-light text-warning'
                  }`}>
                    {editingUser.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Created:</span>
                  <span className="text-sm text-foreground">
                    {editingUser.createdAt ? new Date(editingUser.createdAt).toLocaleDateString() : '-'}
                  </span>
                </div>
              </div>

              {/* Editable fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-department">Department</Label>
                  <Input
                    id="edit-department"
                    value={editDepartment}
                    onChange={(e) => setEditDepartment(e.target.value)}
                    placeholder="e.g., Avionics Engineering"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  {/* eslint-disable-next-line no-restricted-syntax -- Native select for role picker, shadcn Select would require significant refactoring */}
                  <select
                    id="edit-role"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={editingUser.email === 'admin@aerotage.com'}
                    className="w-full p-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="">No Role</option>
                    {roleGroups.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  {editingUser.email === 'admin@aerotage.com' && (
                    <p className="text-xs text-muted-foreground">
                      System admin role cannot be changed.
                    </p>
                  )}
                </div>
              </div>

              {/* Enable/Disable toggle */}
              {editingUser.email !== 'admin@aerotage.com' && (
                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Account Enabled</p>
                      <p className="text-xs text-muted-foreground">
                        Disabled users cannot sign in to the application.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setEditEnabled(!editEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors p-0 ${
                        editEnabled ? 'bg-success' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                          editEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </Button>
                  </div>
                  {!editEnabled && (
                    <div className="mt-2 p-2 bg-warning-light border border-warning rounded text-warning text-xs">
                      This user will not be able to sign in while disabled.
                    </div>
                  )}
                </div>
              )}

              {editingUser.email === 'admin@aerotage.com' && (
                <div className="border-t border-border pt-4 mt-4">
                  <div className="p-3 bg-info-light border border-info rounded-lg text-info text-sm">
                    This is the protected system administrator account. Role and enabled status cannot be modified.
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              disabled={editSaving}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UsersContent;
